import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import axios from 'axios';
import { Voyageurs } from '../models/Voyageurs';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { API_BASE_URL } from '../config/api';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormNigeria: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États initiaux
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const [informations, setInformations] = useState<string>(state?.informations || '');
  const commentaires = state?.commentaires || '';

  // Champs de base
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');

  // Champs spécifiques Nigeria
  const [dateEntreeNigeria, setDateEntreeNigeria] = useState<string>('');
  const [villeEntreeNigeria, setVilleEntreeNigeria] = useState<string>('');
  const [dateSortieNigeria, setDateSortieNigeria] = useState<string>('');
  const [butVoyage, setButVoyage] = useState<string>('');
  const [adresseNigeria, setAdresseNigeria] = useState<string>('');
  const [nomContactNigeria, setNomContactNigeria] = useState<string>('');
  const [nomEmployeur, setNomEmployeur] = useState<string>('');
  const [secteurActivite, setSecteurActivite] = useState<string>('');

  // Voyageurs et assurance
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Fichiers pour visa Affaires
  const [billetAvionAffaires, setBilletAvionAffaires] = useState<File | null>(null);
  const [reservationHotelAffaires, setReservationHotelAffaires] = useState<File | null>(null);
  const [lettreInvitation, setLettreInvitation] = useState<File | null>(null);
  const [lettreMission, setLettreMission] = useState<File | null>(null);

  // Fichiers pour visa Tourisme
  const [billetAvionTourisme, setBilletAvionTourisme] = useState<File | null>(null);
  const [reservationHotelTourisme, setReservationHotelTourisme] = useState<File | null>(null);
  const [justificatifEmploi, setJustificatifEmploi] = useState<File | null>(null);

  // Options pour le combo But du voyage
  const butsVoyage = ['Affaires', 'Tourisme', 'Transit', 'Études'];

  // Calcul du total
  const total = prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Validation du formulaire
  const validateForm = () => {
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    const errors: string[] = [];

    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('nigeria.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('nigeria.errors.departureDateRequired'));
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push(tp('nigeria.errors.returnDateInvalid'));
      }
    } else if (dateSortie && !sortieDate) {
      errors.push(tp('nigeria.errors.returnDateRequired'));
    }

    // Champs obligatoires
    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) errors.push(t('common.validation.returnDate'));
    if (!dateEntreeNigeria.trim()) errors.push(tp('nigeria.validation.dateEntreeNigeria'));
    if (!villeEntreeNigeria.trim()) errors.push(tp('nigeria.validation.villeEntreeNigeria'));
    if (!dateSortieNigeria.trim()) errors.push(tp('nigeria.validation.dateSortieNigeria'));
    if (!butVoyage.trim()) errors.push(tp('nigeria.validation.butVoyage'));
    if (!adresseNigeria.trim()) errors.push(tp('nigeria.validation.adresseNigeria'));
    if (!nomContactNigeria.trim()) errors.push(tp('nigeria.validation.nomContactNigeria'));
    if (!nomEmployeur.trim()) errors.push(tp('nigeria.validation.nomEmployeur'));
    if (!secteurActivite.trim()) errors.push(tp('nigeria.validation.secteurActivite'));

    // Documents pour Affaires (basé sur le titre du visa)
    if (title.toLowerCase().includes('affaire')) {
      if (!billetAvionAffaires) errors.push(tp('nigeria.validation.billetAvion'));
      if (!reservationHotelAffaires) errors.push(tp('nigeria.validation.reservationHotel'));
      if (!lettreInvitation) errors.push(tp('nigeria.validation.lettreInvitation'));
      if (!lettreMission) errors.push(tp('nigeria.validation.lettreMission'));
    }

    // Documents pour Tourisme (basé sur le titre du visa)
    if (title.toLowerCase().includes('tourisme')) {
      if (!billetAvionTourisme) errors.push(tp('nigeria.validation.billetAvion'));
      if (!reservationHotelTourisme) errors.push(tp('nigeria.validation.reservationHotel'));
      if (!justificatifEmploi) errors.push(tp('nigeria.validation.justificatifEmploi'));
    }

    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
  }, [
    dateDepart,
    dateSortie,
    dateEntreeNigeria,
    villeEntreeNigeria,
    dateSortieNigeria,
    butVoyage,
    adresseNigeria,
    nomContactNigeria,
    nomEmployeur,
    secteurActivite,
    assuranceVisa,
    sliderValue,
    selectedVoyageur,
    billetAvionAffaires,
    reservationHotelAffaires,
    lettreInvitation,
    lettreMission,
    billetAvionTourisme,
    reservationHotelTourisme,
    justificatifEmploi,
  ]);

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture de l'onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (
      window.confirm(t('common.confirmation.unsavedChanges'))
    ) {
      navigate(-1);
    }
  };

  // Formatage de la date (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Soumission
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('validite', validite);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));

      // Champs spécifiques Nigeria
      formData.append('dateEntreeNigeria', dateEntreeNigeria);
      formData.append('villeEntreeNigeria', villeEntreeNigeria);
      formData.append('dateSortieNigeria', dateSortieNigeria);
      formData.append('butVoyage', butVoyage);
      formData.append('adresseNigeria', adresseNigeria);
      formData.append('nomContactNigeria', nomContactNigeria);
      formData.append('nomEmployeur', nomEmployeur);
      formData.append('secteurActivite', secteurActivite);

      // Fichiers Affaires (basé sur le titre du visa)
      if (title.toLowerCase().includes('affaire')) {
        if (billetAvionAffaires) formData.append('billetAvion', billetAvionAffaires);
        if (reservationHotelAffaires) formData.append('reservationHotel', reservationHotelAffaires);
        if (lettreInvitation) formData.append('lettreInvitation', lettreInvitation);
        if (lettreMission) formData.append('lettreMission', lettreMission);
      }

      // Fichiers Tourisme (basé sur le titre du visa)
      if (title.toLowerCase().includes('tourisme')) {
        if (billetAvionTourisme) formData.append('billetAvion', billetAvionTourisme);
        if (reservationHotelTourisme) formData.append('reservationHotel', reservationHotelTourisme);
        if (justificatifEmploi) formData.append('justificatifEmploi', justificatifEmploi);
      }

      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setDateEntreeNigeria('');
    setVilleEntreeNigeria('');
    setDateSortieNigeria('');
    setButVoyage('');
    setAdresseNigeria('');
    setNomContactNigeria('');
    setNomEmployeur('');
    setSecteurActivite('');
    setBilletAvionAffaires(null);
    setReservationHotelAffaires(null);
    setLettreInvitation(null);
    setLettreMission(null);
    setBilletAvionTourisme(null);
    setReservationHotelTourisme(null);
    setJustificatifEmploi(null);
  };

  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors societe) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Creer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            backgroundColor: '#fff',
            p: 3,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* Dates de voyage */}
          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Champs spécifiques Nigeria */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {tp('nigeria.sections.sejourNigeria')}
          </Typography>

          <TextField
            label={tp('nigeria.fields.dateEntreeNigeria.label')}
            placeholder={tp('nigeria.fields.dateEntreeNigeria.placeholder')}
            value={dateEntreeNigeria}
            onChange={(e) => setDateEntreeNigeria(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('nigeria.fields.villeEntreeNigeria.label')}
            placeholder={tp('nigeria.fields.villeEntreeNigeria.placeholder')}
            value={villeEntreeNigeria}
            onChange={(e) => setVilleEntreeNigeria(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('nigeria.fields.dateSortieNigeria.label')}
            placeholder={tp('nigeria.fields.dateSortieNigeria.placeholder')}
            value={dateSortieNigeria}
            onChange={(e) => setDateSortieNigeria(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="but-voyage-label">{tp('nigeria.fields.butVoyage')}</InputLabel>
            <Select
              labelId="but-voyage-label"
              id="but-voyage-select"
              value={butVoyage}
              label={tp('nigeria.fields.butVoyage')}
              onChange={(e: SelectChangeEvent) => setButVoyage(e.target.value)}
            >
              {butsVoyage.map((but) => (
                <MenuItem key={but} value={but}>
                  {but}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={tp('nigeria.fields.adresseNigeria.label')}
            placeholder={tp('nigeria.fields.adresseNigeria.placeholder')}
            value={adresseNigeria}
            onChange={(e) => setAdresseNigeria(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            label={tp('nigeria.fields.nomContactNigeria.label')}
            placeholder={tp('nigeria.fields.nomContactNigeria.placeholder')}
            value={nomContactNigeria}
            onChange={(e) => setNomContactNigeria(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Informations employeur */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {tp('nigeria.sections.informationsProfessionnelles')}
          </Typography>

          <TextField
            label={tp('nigeria.fields.nomEmployeur.label')}
            placeholder={tp('nigeria.fields.nomEmployeur.placeholder')}
            value={nomEmployeur}
            onChange={(e) => setNomEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('nigeria.fields.secteurActivite.label')}
            placeholder={tp('nigeria.fields.secteurActivite.placeholder')}
            value={secteurActivite}
            onChange={(e) => setSecteurActivite(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Documents pour Affaires (basé sur le titre du visa) */}
          {title.toLowerCase().includes('affaire') && (
            <Box sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {tp('nigeria.sections.documentsAffaires')}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('nigeria.fields.billetAvion')}
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setBilletAvionAffaires(e.target.files?.[0] || null)}
                />
                {billetAvionAffaires && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tp('nigeria.fileSelected', { name: billetAvionAffaires.name })}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('nigeria.fields.reservationHotel')}
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setReservationHotelAffaires(e.target.files?.[0] || null)}
                />
                {reservationHotelAffaires && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tp('nigeria.fileSelected', { name: reservationHotelAffaires.name })}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('nigeria.fields.lettreInvitation')}
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setLettreInvitation(e.target.files?.[0] || null)}
                />
                {lettreInvitation && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tp('nigeria.fileSelected', { name: lettreInvitation.name })}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('nigeria.fields.lettreMission')}
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setLettreMission(e.target.files?.[0] || null)}
                />
                {lettreMission && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tp('nigeria.fileSelected', { name: lettreMission.name })}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Documents pour Tourisme (basé sur le titre du visa) */}
          {title.toLowerCase().includes('tourisme') && (
            <Box sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {tp('nigeria.sections.documentsTourisme')}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('nigeria.fields.billetAvion')}
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setBilletAvionTourisme(e.target.files?.[0] || null)}
                />
                {billetAvionTourisme && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tp('nigeria.fileSelected', { name: billetAvionTourisme.name })}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('nigeria.fields.reservationHotel')}
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setReservationHotelTourisme(e.target.files?.[0] || null)}
                />
                {reservationHotelTourisme && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tp('nigeria.fileSelected', { name: reservationHotelTourisme.name })}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('nigeria.fields.justificatifEmploi')}
                </Typography>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setJustificatifEmploi(e.target.files?.[0] || null)}
                />
                {justificatifEmploi && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    {tp('nigeria.fileSelected', { name: justificatifEmploi.name })}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Module Assurance */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif du total */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* État du formulaire */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormNigeria;
